import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useSearchFilterValuesMutation } from "../../../../dataApiSlice";

// Debounce utility to reduce API calls
const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export default function CheckBoxComp({
  data,
  selectedFilterValues,
  setSelectedFilterValues,
  setIsAll,
  columnName,
  dataSourceID,
}) {
  /** ---------- Derived Data (Memoized) ---------- **/
  const allOptionsData = useMemo(() => {
    const values = Object.values(data ?? {})[0] ?? [];
    return values.map((v) => (v === null ? "NULL" : v === "" ? "BLANK" : v));
  }, [data]);

  /** ---------- Local State ---------- **/
  const [filteredOptions, setFilteredOptions] = useState(allOptionsData);
  const [searchTerm, setSearchTerm] = useState("");

  /** ---------- API Mutation ---------- **/
  const [
    searchFilterValues,
    { data: searchData, isLoading, isError, isSuccess, error },
  ] = useSearchFilterValuesMutation();

  /** ---------- Debounced Search ---------- **/
  const debouncedSearch = useDebounce(searchTerm, 400);

  useEffect(() => {
    if (debouncedSearch.trim()) {
      const body = {
        dataSourceID,
        queries: [debouncedSearch],
        columnName,
      };
      searchFilterValues(body);
    } else {
      const filteredOptions = allOptionsData.filter((option) => {
        if (typeof option === "string") {
          return option.toLowerCase().includes(debouncedSearch.toLowerCase());
        } else if (typeof option === "number") {
          return option.toString().toLowerCase().includes(debouncedSearch.toLowerCase());
        }
      });
      setFilteredOptions(filteredOptions);
    }
  }, [debouncedSearch, allOptionsData, dataSourceID, columnName, searchFilterValues]);

  /** ---------- Update filtered options when API returns ---------- **/
  useEffect(() => {
    if (isSuccess && searchData?.results) {
      setFilteredOptions(searchData.results);
    }
  }, [isSuccess, searchData]);

  /** ---------- Handlers (Memoized) ---------- **/
  const handleCheckboxChange = useCallback(
    (value) => {
      setSelectedFilterValues((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    },
    [setSelectedFilterValues]
  );

  const handleSelectAllToggle = useCallback(() => {
    setSelectedFilterValues((prev) =>
      prev.length === allOptionsData.length ? [] : allOptionsData
    );
  }, [allOptionsData, setSelectedFilterValues]);

  /** ---------- Derived Booleans ---------- **/
  const isAllSelected = useMemo(
    () => selectedFilterValues.length === filteredOptions.length,
    [selectedFilterValues, filteredOptions]
  );

  /** ---------- Sync All-Select Status ---------- **/
  useEffect(() => {
    setIsAll?.(isAllSelected);
  }, [isAllSelected, setIsAll]);

  /** ---------- JSX ---------- **/
  return (
    <main className="h-auto flex flex-col items-center justify-start gap-2 w-full">
      {/* 🔍 Search */}
      <section className="flex flex-col items-center justify-center gap-2 w-full">
        <div className="flex items-center justify-center w-[104%] relative -left-[8px]">
          <FaMagnifyingGlass className="relative left-6 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            className="border rounded border-gray-200 pl-8 pr-2 py-1 w-full select-custom focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <label
          htmlFor="select-all-checkbox"
          className="flex items-center justify-start text-left w-full relative left-[2px] cursor-pointer"
        >
          <input
            type="checkbox"
            id="select-all-checkbox"
            checked={isAllSelected}
            onChange={handleSelectAllToggle}
            className="mr-[6px] checkbox-input"
          />
          Select All
        </label>
      </section>

      {/* ✅ Checkbox List */}
      <section
        className={`overflow-auto py-1 px-1 border w-full rounded h-32`}
      >
        {isLoading && <p>Finding related values...</p>}
        {isError && (
          <p>{error?.data?.error || error?.data || "An error occurred"}</p>
        )}

        {!isLoading && filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const isChecked = selectedFilterValues.includes(option);
            return (
              <label
                key={option ?? index}
                className="flex items-center mb-1 cursor-pointer truncate hover:text-[#5ac189]"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleCheckboxChange(option)}
                  className="mr-[6px] checkbox-input"
                />
                {option}
              </label>
            );
          })
        ) : (
          !isLoading && <p className="text-gray-400">No options found.</p>
        )}
      </section>
    </main>
  );
}
